import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

// Error boundary for better debugging
// Track recent errors to prevent spam
const recentErrors = new Map<string, number>();
const ERROR_THROTTLE_MS = 1000; // Only log same error once per second

window.addEventListener('error', (event) => {
  // Only handle real errors, ignore null errors and script loading errors
  if (!event.error && !event.message) {
    return; // Ignore null errors
  }
  
  // Ignore ResizeObserver errors - these are common with ReactFlow and harmless
  // They occur when ResizeObserver callbacks trigger layout changes
  const errorMessage = event.error?.message || event.message || '';
  const errorString = String(event.error || event.message || '');
  if (errorMessage.includes('ResizeObserver') || 
      errorString.includes('ResizeObserver') ||
      errorMessage.includes('loop completed with undelivered notifications') ||
      errorString.includes('loop completed with undelivered notifications')) {
    return; // Silently ignore ResizeObserver errors
  }
  
  // Ignore script loading errors (these are often false positives)
  if (event.filename && (
    event.filename.includes('chrome-extension') ||
    event.filename.includes('moz-extension') ||
    event.filename.includes('safari-extension')
  )) {
    return;
  }
  
  // Create a unique key for this error to prevent spam
  const errorKey = `${event.message || 'unknown'}-${event.filename || 'unknown'}-${event.lineno || 0}`;
  const now = Date.now();
  const lastLogged = recentErrors.get(errorKey);
  
  // Throttle: only log if we haven't seen this exact error in the last second
  if (lastLogged && (now - lastLogged) < ERROR_THROTTLE_MS) {
    return;
  }
  
  recentErrors.set(errorKey, now);
  
  // Clean up old entries (keep only last 50)
  if (recentErrors.size > 50) {
    const oldestKey = recentErrors.keys().next().value;
    recentErrors.delete(oldestKey);
  }
  
  // Only log if there's actual error information
  if (event.error || event.message) {
    console.error('Global error:', event.error || event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
    
    const rootElement = document.getElementById('root');
    if (rootElement && event.error) {
      rootElement.innerHTML = `
        <div style="padding: 20px; color: red; font-family: monospace;">
          <h1>Application Error</h1>
          <p><strong>Error:</strong> ${event.error?.message || event.message}</p>
          <p><strong>File:</strong> ${event.filename}</p>
          <p><strong>Line:</strong> ${event.lineno}</p>
          <pre>${event.error?.stack || 'No stack trace'}</pre>
        </div>
      `;
    }
  }
}, true);

// Track recent promise rejections to prevent spam
const recentRejections = new Map<string, number>();

window.addEventListener('unhandledrejection', (event) => {
  // Ignore null/undefined rejections
  if (!event.reason) {
    return;
  }
  
  // Create a unique key for this rejection
  const reasonStr = typeof event.reason === 'string' 
    ? event.reason 
    : (event.reason?.message || String(event.reason));
  
  // Ignore ResizeObserver-related promise rejections
  if (reasonStr.includes('ResizeObserver') || 
      reasonStr.includes('loop completed with undelivered notifications')) {
    return; // Silently ignore ResizeObserver rejections
  }
  
  const rejectionKey = reasonStr.substring(0, 100); // Limit key length
  const now = Date.now();
  const lastLogged = recentRejections.get(rejectionKey);
  
  // Throttle: only log if we haven't seen this exact rejection in the last second
  if (lastLogged && (now - lastLogged) < ERROR_THROTTLE_MS) {
    return;
  }
  
  recentRejections.set(rejectionKey, now);
  
  // Clean up old entries
  if (recentRejections.size > 50) {
    const oldestKey = recentRejections.keys().next().value;
    recentRejections.delete(oldestKey);
  }
  
  console.error('Unhandled promise rejection:', event.reason);
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

try {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} catch (error) {
  console.error('Failed to render app:', error);
  rootElement.innerHTML = `
    <div style="padding: 20px; color: red; font-family: monospace;">
      <h1>React Render Error</h1>
      <p><strong>Error:</strong> ${error instanceof Error ? error.message : String(error)}</p>
      <pre>${error instanceof Error ? error.stack : 'No stack trace'}</pre>
    </div>
  `;
}